<template>
    <div
        id="chat-container__stream"
        ref="scrollRoot"
        tabindex="0"
        aria-label="Chat messages"
        :class="{ 'hidden md:flex': showWidgetSidebar, 'overflow-y-auto': showOverflow, 'overflow-y-hidden': !showOverflow }"
        class="grow md:flex snap-y max-md:scroll-pt-20 scroll-pb-5 md:scroll-pb-10 overflow-x-hidden focus:outline-hidden focus-visible:outline">
        <Teleport v-if="!boxed" defer to="#chat-container">
            <div class="blurEffect"></div>
        </Teleport>
        <div :class="['w-full chat_component rounded-xl', boxed ? 'boxed px-2 md:px-4' : 'md:max-w-(--breakpoint-md) md:mx-auto']">
            <!-- `messages` class is used as a selector in the devops repo, for synthetic checks -->
            <div :class="['relative messages max-md:px-4', boxed ? 'pt-6' : showHeader ? 'sm:mt-8 pt-[env(safe-area-inset-top)]' : 'sm:mt-8 pt-10']">
                <Teleport to="body">
                    <Transition name="fade">
                        <div v-if="!showStartLoadingMessage && !hasActivatedCoachingSession" class="fixed top-8 inset-x-0 w-fit mx-auto z-10">
                            <ChatActivation :messages="messages" />
                        </div>
                    </Transition>
                </Teleport>
                <SplashScreen v-if="showHeader" :name="userFirstName" :coaching-session-created-at="coachingSessionCreatedAt" />
                <Transition name="fade">
                    <div v-if="!showStartLoadingMessage" class="fs-mask flex flex-col gap-10">
                        <template v-for="(message, idx) in messages" :key="idx">
                            <template v-if="message.role === 'assistant'">
                                <div>
                                    <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                        <div
                                            v-if="shouldRenderLine(message, line)"
                                            :ref="(ref) => setMessageRef(ref, message.chat_message_id)"
                                            :class="[
                                                'group/chat nadia-chat flex flex-col max-w-full transition-colors duration-300 rounded-lgr',
                                                { 'bg-valence-purple/10': message.chat_message_id === focusedMessage },
                                            ]">
                                            <!-- group/chat is needed for hover to work with ChatMessageReactionBar -->
                                            <article class="grow text-left px-2" :aria-label="`Assistant message ${idx}-${lineIdx}`">
                                                <div class="prose">
                                                    <ChatMessageText
                                                        v-if="line.type === 'md'"
                                                        :message="line.content"
                                                        :message-id="message.chat_message_id"
                                                        :message-obj="message" />
                                                </div>
                                                <ChatMessageToolCall
                                                    v-if="lineIsAction(line) && line.action_name === ACTION.TOOL_CALL"
                                                    :tool="
                                                        // @ts-expect-error - bringing in types for different chat actions in smaller pieces
                                                        toolCalls.get(line.action_params?.tool_call_id)
                                                    "
                                                    :hide-email-button="hideEmailButton" />
                                            </article>
                                            <Transition name="slide-up">
                                                <UIDirectiveRenderer
                                                    v-if="shouldShowUiDirective(line)"
                                                    :data="line as UiDirectiveLine"
                                                    :message-id="message.chat_message_id"
                                                    :line-idx="lineIdx"
                                                    @started="handleRolePlayStarted"
                                                    @canceled="handleRolePlayCanceled" />
                                            </Transition>
                                            <div v-if="shouldIncludeReactionBar(message, lineIdx)" class="flex justify-between items-center px-2">
                                                <ChatMessageReactionBar
                                                    :ref="(ref) => (reactionBarRefs[`rb-${message.chat_message_id}`] = ref)"
                                                    :message="message"
                                                    :is-last-message="idx === messages.length - 1"
                                                    :has-existing-feedback="getMessageFeedbackState(message.chat_message_id).thumbState !== null"
                                                    @thought-process-clicked="handleThoughtProcessClicked" />
                                                <button
                                                    v-if="shouldShowNadiaIntelligence(message)"
                                                    class="flex gap-2 items-center"
                                                    @click="setIsNadiaIntelligenceOpen(message.chat_message_id, !isNadiaIntelligenceOpen(message.chat_message_id))">
                                                    <NadiaIntelligence class="w-5 h-5" /> Nadia Intelligence
                                                    <i
                                                        :class="['bi text-base/none', isNadiaIntelligenceOpen(message.chat_message_id) ? 'bi-chevron-up' : 'bi-chevron-down']"
                                                        aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                    <ChatNadiaIntelligence :message="message" />
                                </div>
                                <PerspectiveNewChatOffer
                                    v-if="perspectiveNewChatOffer && shouldShowPerspectiveOfferAfter(message, idx)"
                                    :offer="perspectiveNewChatOffer"
                                    :coaching-session-uuid="coachingSessionUuid"
                                    class="mx-2 my-4"
                                    @dismiss="emit('perspectiveOfferDismissed')" />
                                <MyWorkInChatInsightPrompt
                                    v-else-if="shouldShowInsightPromptAfter(message, idx) && myWorkInChatInsight"
                                    :insight="myWorkInChatInsight"
                                    :coaching-session-uuid="coachingSessionUuid"
                                    class="mx-2" />
                            </template>
                            <template v-else-if="message.role === 'user'">
                                <div>
                                    <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                        <div v-if="shouldRenderLine(message, line)" class="group/user-msg grid auto-rows-min gap-x-3 gap-y-1 place-items-end body">
                                            <div v-if="isLastIndexOfUserMessage(message.lines, lineIdx) && hasFileActionInLines(message)" class="flex my-1 gap-2 justify-end">
                                                <div class="overflow-auto max-w-xs sm:max-w-lg md:max-w-(--breakpoint-sm) flex gap-2">
                                                    <FileAttachedTag
                                                        v-for="file in getFileActionInLines(message)"
                                                        :key="file.name ?? ''"
                                                        :file="file"
                                                        data-testid="in-chat-file-attached-tag" />
                                                </div>
                                            </div>
                                            <div
                                                :ref="(ref) => setMessageRef(ref, message.chat_message_id)"
                                                :class="[
                                                    'relative flex gap-2 flex-row max-w-full px-4 prose-p:my-4 rounded-lgr',
                                                    {
                                                        'row-start-2': hasFileActionInLines(message),
                                                        'bg-[#F3F3EF]': message.chat_message_id !== focusedMessage,
                                                        'bg-valence-purple/10': message.chat_message_id === focusedMessage,
                                                    },
                                                ]">
                                                <button
                                                    v-if="editAndStopGeneratingEnabled && isLastIndexOfUserMessage(message.lines, lineIdx) && isLastUserMessage(idx) && !waiting"
                                                    type="button"
                                                    class="absolute -left-9 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full text-valence-grey-600 hover:text-valence-grey-800 hover:bg-[#EAEAEA] transition-colors opacity-0 group-hover/user-msg:opacity-100 focus:opacity-100"
                                                    title="Edit message"
                                                    aria-label="Edit message"
                                                    data-testid="edit-message-button"
                                                    @click="handleEditMessage(idx, message)">
                                                    <i class="bi bi-pencil text-sm" aria-hidden="true" />
                                                </button>
                                                <article class="w-full text-left" :aria-label="`User message ${idx}-${lineIdx}`">
                                                    <ChatMessageText
                                                        v-if="line.type === 'md'"
                                                        :message="line.content"
                                                        :message-id="message.chat_message_id"
                                                        :message-obj="message" />
                                                    <button
                                                        v-if="isLastIndexOfUserMessage(message.lines, lineIdx) && showRolePlayInsightButton(message)"
                                                        type="button"
                                                        class="bg-[#F7FAFF]/75 hover:bg-[#F7FAFF] body-small mb-2 px-2 py-1 rounded-full text-valence-primary-500 border-2 border-valence-primary-500/40"
                                                        @click="focusRolePlayFeedback(message.chat_message_id)">
                                                        View suggestion
                                                    </button>
                                                </article>
                                            </div>
                                            <ChatMyPeopleAccordion
                                                v-if="shouldShowConnection(line)"
                                                :execute="execute"
                                                :connection-chat-message-action="{ line: line as ParseActionsTaggedPerson, lineIdx, message }"
                                                data-testid="new-connection"
                                                class="py-2 w-full max-w-92"
                                                should-show-submitted />
                                            <ChatAgentToolBackToPMOverview v-if="lineIsAction(line) && line.action_name === ACTION.BACK_TO_OVERVIEW" :line="line" class="mt-4" />
                                            <ChatAgentToolPushReviewToWorkday
                                                v-if="lineIsAction(line) && line.action_name === ACTION.PUSH_REVIEW_TO_WORKDAY"
                                                :line="line"
                                                :line-idx="lineIdx"
                                                :message-id="message.chat_message_id"
                                                class="mt-4" />
                                            <ChatAgentToolPushManagerReviewToWorkday
                                                v-if="lineIsAction(line) && line.action_name === ACTION.PUSH_MANAGER_REVIEW_TO_WORKDAY"
                                                :line="line"
                                                :line-idx="lineIdx"
                                                :message-id="message.chat_message_id"
                                                class="mt-4" />
                                            <ChatLoadingFrame
                                                v-if="lineIsAction(line) && line.action_name === ACTION.LOADING"
                                                class="mt-4 place-self-start"
                                                :is-loading="!line.action_params?.loaded">
                                                {{ line.action_params?.content }}
                                            </ChatLoadingFrame>
                                        </div>
                                    </template>
                                </div>
                            </template>
                            <template v-else-if="message.role === 'tool'">
                                <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                    <slot name="tool-result" :line="line" :message="message" :line-idx="lineIdx" />
                                </template>
                            </template>
                        </template>
                    </div>
                </Transition>
                <div aria-live="polite">
                    <CoachingModeMarble v-if="shouldShowLoadingMessageMarble" class="mt-4" size="small" is-loading />
                    <ChatWaitingMessage v-else-if="showSlowResponse" :messages="['One moment while I gather my thoughts...']" class="my-4" />
                    <ChatWaitingMessage v-else-if="isWaitingForInitialResponse" :messages="['Waiting for Nadia...']" class="my-4" />
                    <ChatThinkingMessage v-else-if="isThinking" :messages="thinkingMessages" class="my-4" />
                    <GeneralCoachMarble v-else-if="lastMessageIsAssistant" :class="['mt-4 size-(--spacing-awaiting-marble)', { loadingMarble: !lastMessage?.id }]" />
                </div>
                <div ref="bottomAnchor" class="pb-24 md:pb-16" :style="{ height: bottomHeight }" />
                <Transition name="fade">
                    <div v-if="!isBottomVisible" class="hidden md:block h-0 sticky bottom-2">
                        <button
                            type="button"
                            class="leading-none absolute w-fit bottom-0 left-1/2 -translate-x-1/2 body-small rounded-full bg-white text-valence-grey-750 py-3 px-6 border border-border"
                            data-testid="scroll-to-latest-button"
                            @click="scrollToLastMessage">
                            scroll <i class="bi bi-chevron-down leading-none" aria-hidden="true"></i>
                        </button>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints, useElementVisibility } from "@vueuse/core";
import { useFetch } from "~vue/api";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { type ComponentPublicInstance, computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

import type { GlobalProperties } from "./appTypes.js";
import { ACTION, ChatAction, getRolePlayMonitorParams, isRolePlayMessage, lineIsAction } from "./chatActions.js";
import ChatActivation from "./ChatDetail/ChatActivation.vue";
import ChatAgentToolBackToPMOverview from "./ChatDetail/ChatAgentToolBackToPMOverview.vue";
import ChatAgentToolPushManagerReviewToWorkday from "./ChatDetail/ChatAgentToolPushManagerReviewToWorkday.vue";
import ChatAgentToolPushReviewToWorkday from "./ChatDetail/ChatAgentToolPushReviewToWorkday.vue";
import ChatMyPeopleAccordion from "./ChatDetail/ChatMyPeopleAccordion.vue";
import ChatNadiaIntelligence from "./ChatDetail/ChatNadiaIntelligence.vue";
import MyWorkInChatInsightPrompt from "./ChatDetail/MyWorkInChatInsightPrompt.vue";
import PerspectiveNewChatOffer from "./ChatDetail/PerspectiveNewChatOffer.vue";
import SplashScreen from "./ChatDetail/SplashScreen.vue";
import type { MyWorkInChatInsight, PerspectiveNewChatOffer as PerspectiveNewChatOfferProp } from "./ChatDetail/types";
import UIDirectiveRenderer from "./ChatDetail/UIDirectiveRenderer.vue";
import ChatLoadingFrame from "./ChatLoadingFrame.vue";
import ChatMessageReactionBar from "./ChatMessageReactionBar.vue";
import ChatMessageText from "./ChatMessageText.vue";
import ChatMessageToolCall from "./ChatMessageToolCall.vue";
import ChatThinkingMessage from "./ChatThinkingMessage.vue";
import ChatWaitingMessage from "./ChatWaitingMessage.vue";
import FileAttachedTag from "./components/FileAttachedTag.vue";
import CoachingModeMarble from "./components/navigation/CoachingModeMarble.vue";
import GeneralCoachMarble from "./components/navigation/CoachingModeMarbles/GeneralCoachMarble.vue";
import { useActionHandler } from "./composables/useActionHandler.js";
import { nadiaIntelligenceActions, useNadiaIntelligence } from "./composables/useNadiaIntelligence.js";
import { CHAT_EVENT } from "./events";
import NadiaIntelligence from "./icons/NadiaIntelligence.vue";
import type {
    ChatMessage,
    ChatMessageLine,
    InChatRolePlayData,
    ParseActionsEmailDraftAction,
    ParseActionsGenericAction,
    ParseActionsProactiveCoachingOfferAction,
    ParseActionsRolePlaySetupAction,
    ParseActionsRolePlaySummaryAction,
    ParseActionsTaggedPerson,
} from "./types/chat.js";
import type { ThumbDirection, TurnFeedback } from "./types/feedback.js";
import { resolveDirective } from "./uiDirectiveRegistry";

const coachingSessionId = inject<string>("coachingSessionId");
const coachingSessionUuid = inject<string>("coachingSessionUuid");
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

type ChatMessageListProps = {
    messages: Array<ChatMessage>;
    // Live progress text for in-flight tool calls, keyed by tool_call_id (see
    // ChatDetail.vue's handleToolCallStatus). Merged into `toolCalls` below.
    toolCallStatusText?: Map<string, string>;
    showOverflow?: boolean;
    showSlowResponse?: boolean;
    showStartLoadingMessage?: boolean;
    showWidgetSidebar?: boolean;
    waiting?: boolean;
    isWaitingForInitialResponse?: boolean;
    isThinking?: boolean;
    userFirstName?: string;
    hasActivatedCoachingSession?: boolean;
    coachingSessionCreatedAt: string;
    showHeader?: boolean;
    hideEmailButton?: boolean;
    editAndStopGeneratingEnabled?: boolean;
    // Boxed PM-chat variant: the list scrolls inside a bordered card (title row above it),
    // so the viewport-cap on .messages and the breakpoint-md centering don't apply.
    boxed?: boolean;
    myWorkInChatInsight?: MyWorkInChatInsight | null;
    perspectiveNewChatOffer?: PerspectiveNewChatOfferProp | null;
    proactiveCoachingOfferEnabled?: boolean;
};

const {
    messages = [],
    toolCallStatusText = new Map(),
    showOverflow = true,
    showSlowResponse = false,
    showStartLoadingMessage = false,
    showWidgetSidebar = false,
    waiting = false,
    isWaitingForInitialResponse = false,
    isThinking = false,
    userFirstName,
    hasActivatedCoachingSession = false,
    coachingSessionCreatedAt,
    showHeader = true,
    hideEmailButton = false,
    editAndStopGeneratingEnabled = false,
    boxed = false,
    myWorkInChatInsight = null,
    perspectiveNewChatOffer = null,
    proactiveCoachingOfferEnabled = false,
} = defineProps<ChatMessageListProps>();

const emit = defineEmits<{
    rolePlayCanceled: [];
    inChatRolePlayStarted: [InChatRolePlayData];
    dismissFailure: [];
    perspectiveOfferDismissed: [];
}>();

// Whimsical thinking messages during response generation
const thinkingMessages = [
    "Nadia is thinking...",
    "Pondering your question...",
    "Crafting a thoughtful response...",
    "Connecting the dots...",
    "Reflecting on this...",
    "Considering all angles...",
    "Gathering insights...",
    "Thinking it through...",
    "Organizing my thoughts...",
    "Searching for the right words...",
    "Processing this carefully...",
    "Weighing the possibilities...",
    "Exploring different perspectives...",
    "Synthesizing ideas...",
    "Formulating a response...",
    "Drawing from experience...",
    "Tapping into coaching wisdom...",
];

const focusedMessage = ref<number | null>(null);

const hasEverHadVisibleUserMessage = ref(false);
watch(
    () => messages.some((message) => message.role === "user" && !message.hidden),
    (hasVisibleUserMessage) => {
        if (hasVisibleUserMessage) {
            hasEverHadVisibleUserMessage.value = true;
        }
    },
    { immediate: true },
);
const firstCompletedAssistantMessageIndex = computed(() =>
    messages.findIndex(
        (message) =>
            message.role === "assistant" &&
            !message.hidden &&
            Boolean(message.id) &&
            message.lines.some((line) => line.type === "md" && !line.hidden && Boolean(line.content.trim())),
    ),
);

const shouldShowInsightPromptAfter = (message: ChatMessage, messageIndex: number) =>
    Boolean(myWorkInChatInsight) && !hasEverHadVisibleUserMessage.value && Boolean(message.id) && messageIndex === firstCompletedAssistantMessageIndex.value;

// The offer keeps its place after the user replies; only an explicit dismiss removes it.
const shouldShowPerspectiveOfferAfter = (message: ChatMessage, messageIndex: number) => Boolean(message.id) && messageIndex === firstCompletedAssistantMessageIndex.value;

// Feedback Relevant
const showReport = ref(false);

// Reaction bar refs for initializing feedback state
const reactionBarRefs = ref<Record<string, Element | ComponentPublicInstance | InstanceType<typeof ChatMessageReactionBar> | null>>({});
const messageFeedback = ref<Record<number, { thumbState: ThumbDirection | null; isSubmitting: boolean }>>({});
const existingFeedback = ref([]);

const getMessageFeedbackState = (messageId: number) => {
    return (
        messageFeedback.value[messageId] || {
            thumbState: null,
            isSubmitting: false,
        }
    );
};

const lastMessage = computed(() => messages.at(-1));

const lastMessageIsAssistant = computed(() => lastMessage.value?.role === "assistant");

const shouldShowLoadingMessageMarble = computed(() => {
    // Don't show the marble if we're in one of the new thinking states
    if (isWaitingForInitialResponse || isThinking) {
        return false;
    }

    // Show marble if latest message is from user
    return lastMessage.value?.role === "user" && !lastMessage.value.hidden;
});

const loadExistingFeedback = async () => {
    try {
        const { data } = await useFetch<TurnFeedback[]>(`/api/chat/feedback/turn-feedback/coaching-session/${coachingSessionId}/`).get().json();

        const response = data.value;
        existingFeedback.value = response;

        // Initialize reaction bar components with existing feedback
        nextTick(() => {
            response.forEach((feedback: TurnFeedback) => {
                if (feedback.feedback_kind === "thumb" && feedback.feedback_data?.value !== undefined) {
                    const thumbState = feedback.feedback_data.value === 1 ? "up" : "down";
                    const reactionBarRef = reactionBarRefs.value[`rb-${feedback.message_id}`] as InstanceType<typeof ChatMessageReactionBar>;
                    if (reactionBarRef.initializeFeedbackState) {
                        reactionBarRef.initializeFeedbackState({ thumbState });
                    }
                }
            });
        });
    } catch (error) {
        logError(error, { context: { coaching_session_id: coachingSessionId, operation: "load_existing_feedback" } });
    }
};

// Spacer below the last message, so a freshly sent user message has somewhere to
// scroll to. The full-page chat fills the viewport, so it can size the spacer off
// `dvh`; the boxed variant scrolls inside a card of unknown height, so it measures
// the container instead (see `sizeSpacerToPinLastMessage`).
const bottomHeight = ref<string>("0");
const VIEWPORT_SPACER_HEIGHT = "calc(100dvh - 450px)";
watch(
    () => messages,
    () => {
        if (boxed || messages.length === 0) {
            return;
        }
        const latestMessage = messages[messages.length - 1];
        if (latestMessage.role === "user" && !latestMessage.hidden) {
            // 450px is roughly the height of the top and bottom portions that need to be taken into account, on both desktop and mobile
            bottomHeight.value = VIEWPORT_SPACER_HEIGHT;
        }
    },
    { deep: true },
);
watch(
    () => showStartLoadingMessage,
    (newValue) => {
        if (!newValue) {
            if (!boxed) {
                bottomHeight.value = VIEWPORT_SPACER_HEIGHT;
            }
            nextTick(() => {
                scrollToLastMessage();
            });
        }
    },
    { once: true },
);

onMounted(() => {
    emitter.on(CHAT_EVENT.FOCUS_CHAT_MESSAGE, handleFocusChatMessage);
    emitter.on("show_feedback_report", handleShowReport);
    emitter.on("first_socket_message", loadExistingFeedback);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.FOCUS_CHAT_MESSAGE, handleFocusChatMessage);
    emitter.off("show_feedback_report", handleShowReport);
    emitter.off("first_socket_message", loadExistingFeedback);
});

const toolCalls = computed(() => {
    /*
     * Computes a map that contains all existing tool calls
     * and their associated data. The map looks like this:
     *
     * [key: string]: {
     *    // Name of the tool. Matches the corresponding action's name.
     *    name: String,
     *    // Has the tool completed its run?
     *    completed: Boolean,
     *    // The chat message action containing the tool's returned data
     *    action: ChatMessageAction,
     *    // Message id of the message containing the tool's returned data
     *    messageId: Number,
     *    // Message line index of containing the tool's chat message action
     *    lineIdx: Number
     * }
     *
     * This function runs through `messages` once and constructs the tool call
     * based on the current state of the message stack. The data associated to
     * a tool run is spread across multiple messages. When a tool call is requested,
     * the back-end creates an `assistant` message that has a `tool` line. At this
     * point we know that a tool run for a given action is taking place, and so
     * create the "running" state for the tool in this map. We only know that tool run's
     * id and the name of the action that will eventually unfold.
     *
     * When a tool run completes, the back-end creates a `tool` message that has an
     * `action` line containing the result of the tool call. We must inspect the
     * action's `action_params` to detect a tool call id. If found, the tool call
     * state is updated to mark it as completed and populate the
     * corresponding ChatMessageAction.
     */
    const map = new Map<
        string,
        {
            name: string;
            completed: boolean;
            action: ChatMessageLine | null;
            messageId: number | null;
            lineIdx: number | null;
            // Live progress text while running (see `toolCallStatusText` prop); null once
            // completed or when no status has arrived yet — ChatMessageToolCall falls back
            // to its static per-tool label in either case.
            statusText: string | null;
        }
    >();
    for (let message of messages) {
        if (!["assistant", "tool"].includes(message.role)) {
            continue;
        }

        if (message.role === "assistant") {
            for (let line of message.lines) {
                if (!lineIsAction(line)) {
                    continue;
                }

                // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                if (line.action_name === ACTION.TOOL_CALL && !map.has(line.action_params.tool_call_id)) {
                    // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                    map.set(line.action_params.tool_call_id, {
                        name: line.action_params.tool_name,
                        completed: false,
                        action: null,
                        messageId: null,
                        lineIdx: null,
                        statusText: toolCallStatusText.get(line.action_params.tool_call_id) ?? null,
                    });
                }
            }
        }

        if (message.role === "tool") {
            for (let line of message.lines) {
                if (!lineIsAction(line)) {
                    continue;
                }

                // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                if (line.action_params?.tool_call_id && map.has(line.action_params.tool_call_id)) {
                    // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                    map.set(line.action_params.tool_call_id, {
                        // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                        ...map.get(line.action_params.tool_call_id),
                        completed: true,
                        action: line,
                        messageId: message.chat_message_id,
                        lineIdx: message.lines.indexOf(line),
                    });
                }
            }
        }
    }

    return map;
});

const messageRefs = ref<Record<string, Element | ComponentPublicInstance>>({});
const refForMessage = (chatMessageId: number) => `m-${chatMessageId}`;
const setMessageRef = (ref: Element | ComponentPublicInstance | null, chatMessageId: number) => {
    if (ref) {
        messageRefs.value[refForMessage(chatMessageId)] = ref;
    }
};

const handleShowReport = () => {
    logUserInteraction("feedback_report_toggled", {}, coachingSessionUuid);
    showReport.value = !showReport.value;
};

// Long sessions render markdown/code blocks/tool calls across multiple frames,
// so the target message ref may not be registered when handleFocusChatMessage
// first fires. Poll for ~1s, then re-check viewport after layout settles in
// case content above the target grew and pushed it out of view.
const FOCUS_POLL_MAX_ATTEMPTS = 10;
const FOCUS_POLL_INTERVAL_MS = 100;
const FOCUS_SETTLE_RECHECK_MS = 600;
const FOCUS_HIGHLIGHT_DURATION_MS = 1000;

const handleFocusChatMessage = ({ chatMessageId }: { chatMessageId: number }) => {
    scrollToMessageWhenReady(chatMessageId, FOCUS_POLL_MAX_ATTEMPTS);
};

const scrollToMessageWhenReady = (chatMessageId: number, attemptsLeft: number) => {
    const messageRef = messageRefs.value[refForMessage(chatMessageId)];
    if (!(messageRef instanceof HTMLElement)) {
        if (attemptsLeft > 0) {
            setTimeout(() => scrollToMessageWhenReady(chatMessageId, attemptsLeft - 1), FOCUS_POLL_INTERVAL_MS);
        }
        return;
    }
    messageRef.scrollIntoView({ block: "center", behavior: "smooth" });
    focusedMessage.value = chatMessageId;
    nextTick(() => {
        setTimeout(() => {
            focusedMessage.value = null;
        }, FOCUS_HIGHLIGHT_DURATION_MS);
    });
    // Re-scroll after layout has had a chance to settle, in case messages above
    // the target grew during the smooth-scroll animation and shifted it out of view.
    setTimeout(() => {
        const settledRef = messageRefs.value[refForMessage(chatMessageId)];
        const root = scrollRoot.value;
        if (!(settledRef instanceof HTMLElement) || !root) {
            return;
        }
        const rootRect = root.getBoundingClientRect();
        const rect = settledRef.getBoundingClientRect();
        const inViewport = rect.top >= rootRect.top && rect.bottom <= rootRect.bottom;
        if (!inViewport) {
            settledRef.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, FOCUS_SETTLE_RECHECK_MS);
};

const showRolePlayInsightButton = (message: ChatMessage) => {
    if (!isRolePlayMessage(message)) {
        return false;
    }

    const params = getRolePlayMonitorParams(message);
    return params && !!params.feedback;
};

const focusRolePlayFeedback = (chatMessageId: number) => {
    // Open the sidebar if it's closed, and focus the feedback on the next tick.
    emitter.emit(CHAT_EVENT.FOCUS_WIDGET, { actionName: ACTION.ROLE_PLAY_MONITOR });
    nextTick(() => {
        emitter.emit(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, chatMessageId);
    });
};

const handleThoughtProcessClicked = (message: ChatMessage) => {
    emitter.emit(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, message.id);
};

// The bar reacts to the whole message, so it goes on the last line that shows the user something
// (prose or a card). Rendering it per line repeats it around an email draft card, and anchoring it
// to prose alone leaves it above a card that closes the message.
const lastReactionBarLine = (message: ChatMessage, lines: ChatMessageLine[]) =>
    lines.findLastIndex((line) => shouldRenderLine(message, line) && (line.type === "md" || shouldShowUiDirective(line)));

const shouldIncludeReactionBar = (message: ChatMessage, lineIdx: number) => {
    if (!message.lines) {
        return;
    }
    const isCoach = message.role === "assistant";
    const isLastTextLine = lastReactionBarLine(message, message.lines) === lineIdx;
    const isFinishedRendering = !!message.id; // partial chat additions don't yet have a message.id, this comes back from `chat_message` event
    return isCoach && isLastTextLine && isFinishedRendering;
};

const isLastIndexOfUserMessage = (lines: ChatMessageLine[], idx: number) => {
    const lastNonAction = lines.findLastIndex((l) => l.type !== "action");
    // File-only message (no typed content): anchor decorations like the
    // FileAttachedTag chip to the first line instead of returning -1, which
    // would suppress rendering entirely.
    if (lastNonAction === -1) {
        return idx === 0;
    }
    return lastNonAction === idx;
};

const isLastUserMessage = (msgIdx: number) => {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
            return i === msgIdx;
        }
    }
    return false;
};

const handleEditMessage = (msgIdx: number, message: ChatMessage) => {
    const mdLines = message.lines.filter((l) => l.type === "md");
    const content = mdLines.map((l) => ("content" in l ? l.content : "")).join("\n");
    emitter?.emit(CHAT_EVENT.EDIT_MESSAGE, {
        messageIdx: msgIdx,
        content,
        chatMessageUuid: message.chat_message_uuid,
    });
};
const hasFileActionInLines = (message: ChatMessage) =>
    message.lines.some((l) => (l?.type === "action" && l?.action_name === ACTION.FILE_UPLOAD) || (message.files?.length ?? 0) > 0);
const getFileActionInLines = (message: ChatMessage) => {
    const filesFromActionParams = message.lines.find((l) => l?.type === "action" && l?.action_name === ACTION.FILE_UPLOAD && l?.action_params.length > 0);

    if (filesFromActionParams?.type === "action" && filesFromActionParams.action_name === ACTION.FILE_UPLOAD) {
        return filesFromActionParams.action_params ?? message.files;
    }
    return message.files;
};

const bottom = useTemplateRef("bottomAnchor");
const isBelowMd = useBreakpoints(breakpointsTailwind).smaller("md");
// Scrolls the chat stream to the latest message. Two branches:
//
// 1. Mobile, last message is from the user: anchor that message near the top
//    of the scroll container (offset by `scroll-pt-20` = 80px so it sits below
//    the floating widget-toggle button, which is md:hidden on desktop). We
//    scroll the container directly rather than calling scrollIntoView on the
//    message element, because iOS Safari otherwise chains the scroll onto the
//    visual viewport and drifts fixed top-of-viewport elements off-screen.
//
//    The boxed PM variant takes this branch at every breakpoint: its scroll
//    container is a card rather than the viewport, so the viewport-sized spacer
//    the other branch relies on overshoots and carries the message off the top.
//
// 2. Full-page chat, last message is not a visible user message (Nadia's reply,
//    tool results, hidden context messages): collapse the now-stale spacer, then
//    scroll the bottomAnchor into view with block: "end", so the scroll ends at
//    real content instead of blank space (ENG-1272). Boxed is excluded — its
//    spacer is measured to the card, not viewport-sized (ENG-1542).
//
// 3. Everything else (desktop visible user message, initial load, boxed with a
//    non-user tail): scroll the bottomAnchor into view with block: "end", which
//    is the historical behavior.
const scrollRoot = ref<HTMLElement | null>(null);
const scrollPaddingTop = (root: HTMLElement) => parseFloat(getComputedStyle(root).scrollPaddingTop) || 0;

// Grow the spacer to exactly the room the message needs to reach the top of the
// container — no more, so scrolling to the bottom can't strand the reader in blank space.
const sizeSpacerToPinLastMessage = (el: HTMLElement, root: HTMLElement) => {
    const spacer = bottom.value;
    if (!spacer) {
        return;
    }
    const contentBelowMessageTop = spacer.offsetTop - el.offsetTop;
    bottomHeight.value = `${Math.max(0, root.clientHeight - scrollPaddingTop(root) - contentBelowMessageTop)}px`;
};

const pinMessageToTop = (el: HTMLElement, root: HTMLElement) => {
    const offset = el.getBoundingClientRect().top - root.getBoundingClientRect().top;
    root.scrollTo({ top: root.scrollTop + offset - scrollPaddingTop(root), behavior: "smooth" });
};

const scrollToLastMessage = () => {
    const lastMessage = messages[messages.length - 1];
    const root = scrollRoot.value as unknown as HTMLElement | null;
    if ((isBelowMd.value || boxed) && root && lastMessage?.role === "user" && !lastMessage.hidden) {
        const el = messageRefs.value[refForMessage(lastMessage.chat_message_id)];
        if (el instanceof HTMLElement) {
            if (boxed) {
                sizeSpacerToPinLastMessage(el, root);
                // Let the taller spacer land before scrolling, or the scroll clamps to the old height.
                nextTick(() => pinMessageToTop(el, root));
                return;
            }
            pinMessageToTop(el, root);
            return;
        }
    }
    if (!boxed && lastMessage && (lastMessage.role !== "user" || lastMessage.hidden)) {
        // The viewport-sized spacer was inflated to pin the user's just-sent message near
        // the top and nothing collapses it once anything else becomes the latest message
        // (Nadia's reply, a tool result), so scrolling to the anchor's end would strand
        // the view in blank space below the content — and keep the "scroll" button
        // visible over that blank space (ENG-1272). Collapse it first so the anchor's end
        // is the end of real content. Boxed is excluded: its spacer is measured to the
        // card, not viewport-sized (ENG-1542), so it keeps the historical scroll. The
        // `hidden` check is defensive parity with the watcher and pin branch above: the
        // server currently drops hidden messages before they reach the client, so it
        // can't fire today.
        bottomHeight.value = "0";
        // Let the collapsed spacer land before scrolling, or the target position is stale.
        nextTick(() => bottom.value?.scrollIntoView({ behavior: "smooth", block: "end" }));
        return;
    }
    bottom.value?.scrollIntoView({ behavior: "smooth", block: "end" });
};

// Anchor the latest assistant message's top edge below the scroll
// container's `scroll-padding-top` (set on the root via `max-md:scroll-pt-20`
// — clears the `.blurEffect` fade). Used when the soft keyboard opens and
// the layout has reflowed to the smaller viewport.
const pinLatestAssistantToTop = () => {
    const root = scrollRoot.value as unknown as HTMLElement | null;
    if (!root) {
        return;
    }
    for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== "assistant" || msg.hidden) {
            continue;
        }
        const el = messageRefs.value[refForMessage(msg.chat_message_id)];
        if (el instanceof HTMLElement) {
            pinMessageToTop(el, root);
        }
        return;
    }
};
defineExpose({ scrollToLastMessage, pinLatestAssistantToTop });

const shouldRenderLine = (message: ChatMessage, line: ChatMessageLine) => {
    if (lineIsAction(line)) {
        if (line.action_name === ACTION.WORKIQ_COLLABORATION_CONTEXT && message.role === "tool") {
            return false;
        }
        // Actions handled by ChatNadiaIntelligence are suppressed on assistant messages
        // to avoid empty ghost rows — they render via the Nadia Intelligence collapsible instead.
        if (nadiaIntelligenceActions.includes(line.action_name)) {
            return message.role !== "assistant";
        }
        return line.action_name !== ChatAction.ROLE_PLAY_ADJUST;
    }
    return line.type !== "tool";
};

const isBottomVisible = useElementVisibility(bottom);

const handleRolePlayStarted = (data: InChatRolePlayData) => {
    emit("inChatRolePlayStarted", data);
};

const handleRolePlayCanceled = () => {
    emit("rolePlayCanceled");
};

const shouldShowConnection = (line: ChatMessageLine) => {
    return lineIsAction(line) && line.action_name === ACTION.TAGGED_PERSON;
};

// temporary type to cast the line for ui directives, this requires a better type guard fix
type UiDirectiveLine =
    | ParseActionsRolePlaySetupAction
    | ParseActionsRolePlaySummaryAction
    | ParseActionsProactiveCoachingOfferAction
    | ParseActionsEmailDraftAction
    | ParseActionsGenericAction;

const shouldShowUiDirective = (line: ChatMessageLine) => {
    if (!lineIsAction(line)) {
        return false;
    }
    if (line.action_name === ACTION.PROACTIVE_COACHING_OFFER && !proactiveCoachingOfferEnabled) {
        return false;
    }
    return resolveDirective(line.action_name) !== null;
};
const { execute } = useActionHandler();
const { isNadiaIntelligenceOpen, setIsNadiaIntelligenceOpen, shouldShowNadiaIntelligence } = useNadiaIntelligence();
</script>

<style scoped>
@reference "../../css/tailwind.css";
.chat_component {
    :deep(.prose) {
        font-size: inherit;
    }
    :deep(.prose p) {
        word-break: break-word;
        font-size: inherit;
    }
}

.chat_component .messages {
    height: 100%;
    max-height: 80vh;
}

/* Boxed variant: the surrounding card bounds the height, so the viewport cap would
   truncate the scrollable area. */
.chat_component.boxed .messages {
    max-height: none;
}

.blurEffect {
    @apply absolute h-16 top-0 left-0 w-full pointer-events-none;
    background: linear-gradient(180deg, #fff, transparent);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.loadingMarble {
    animation: spinningMarble 2s cubic-bezier(0.65, 0, 0.25, 1.1) infinite;
    animation-delay: 300ms;
}

@keyframes spinningMarble {
    100% {
        transform: rotate(360deg);
    }
}
</style>
